import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { IconPencil, IconCheck } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { MdOutlineDashboardCustomize } from "react-icons/md"
import { LuLayoutList } from "react-icons/lu"
import { useSavedViews } from "@/contexts/SavedViewsContext"
import { useCustomDashboard } from "@/contexts/CustomDashboardContext"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import { Input } from "@/components/Input"
import { Label } from "@/components/ui/label"

const routeTitles: Record<string, string> = {
  "/breakdown": "Cost Explorer / Breakdown",
  "/activity": "Cost Explorer / Activity",
  "/overview": "Overview",
  "/custom-dashboards": "Custom Dashboards",
  "/custom-dashboards/create-new": "Create Custom Dashboard",
  "/reports": "Reports",
  "/alerts": "Alerts",
  "/organization/api-keys": "Organization / API Keys",
  "/organization/team": "Organization / Team",
  "/organization/settings": "Organization / Settings",
  "/organization/billing": "Organization / Billing",
}

export function SiteHeader() {
  const location = useLocation()
  const navigate = useNavigate()
  const { setIsModalOpen, setIsSidebarOpen } = useSavedViews()
  const {
    dashboardId,
    dashboardName,
    dashboardDescription,
    setDashboardName,
    setDashboardDescription,
    hasChanges,
    isEditModalOpen,
    setIsEditModalOpen,
    setIsSaveModalOpen,
    isSaving,
    isRecommended,
  } = useCustomDashboard()

  // Edit modal local state
  const [editName, setEditName] = useState("")
  const [editDescription, setEditDescription] = useState("")

  // Check if we're on a custom dashboard view page (including new dashboard creation)
  const isCustomDashboardView = location.pathname.startsWith("/custom-dashboards/") &&
    location.pathname !== "/custom-dashboards" &&
    dashboardId !== null

  const title = isCustomDashboardView ? null : (routeTitles[location.pathname] || "Dashboard")

  const isOverview = location.pathname === "/overview"
  const isCostExplorer = location.pathname === "/breakdown" || location.pathname === "/activity"

  const handleCreateClick = () => {
    navigate("/custom-dashboards")
  }

  const handleSaveViewClick = () => {
    setIsModalOpen(true)
  }

  const handleSavedViewsClick = () => {
    setIsSidebarOpen(true)
  }

  // Open edit modal with current values
  const handleEditClick = () => {
    setEditName(dashboardName)
    setEditDescription(dashboardDescription)
    setIsEditModalOpen(true)
  }

  // Save edit modal changes
  const handleEditSave = () => {
    if (!editName.trim()) {
      return
    }
    setDashboardName(editName)
    setDashboardDescription(editDescription)
    setIsEditModalOpen(false)
  }

  // Handle save changes button click
  const handleSaveChanges = async () => {
    setIsSaveModalOpen(true)
  }

  return (
    <>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center justify-between gap-1 px-4 lg:gap-2 lg:px-6">
          {/* Title section */}
          {isCustomDashboardView ? (
            <div className="flex items-center gap-1.5">
              <h1 className="text-base font-medium">Dashboard / {dashboardName}</h1>
              {!isRecommended && (
                <button
                  onClick={handleEditClick}
                  className="flex items-center justify-center size-[24px] rounded-[4px] text-[#8A8F98] hover:text-[#654EDA] hover:bg-[#F2EEFF] transition-colors cursor-pointer"
                  title="Edit dashboard"
                >
                  <IconPencil className="size-3.5" />
                </button>
              )}
            </div>
          ) : (
            <h1 className="text-base font-medium">{title}</h1>
          )}

          <div className="flex items-center gap-2">
            {isOverview && (
              <Button
                onClick={handleCreateClick}
                size="xs"
                className="px-4"
              >
                <MdOutlineDashboardCustomize className="mr-[1px] size-3.5" />
                Create Custom Dashboard
              </Button>
            )}

            {isCostExplorer && (
              <>
                <Button
                  onClick={handleSaveViewClick}
                  size="xs"
                  className="px-4"
                >
                  Save View
                </Button>
                <div className="h-[30px] w-px bg-[lch(83.025_0_282.863)]" />
                <Button
                  onClick={handleSavedViewsClick}
                  size="xs"
                  variant="transparent-outline"
                  className="px-3"
                >
                  <LuLayoutList className="size-3.5" />
                  Saved Views
                </Button>
              </>
            )}

            {isCustomDashboardView && !isRecommended && (
              <Button
                onClick={handleSaveChanges}
                disabled={!hasChanges || isSaving}
                size="xs"
                className="px-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconCheck className="size-3.5" />
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Edit Dashboard Modal */}
      <Modal open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              Edit Dashboard
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Update the name and description of your dashboard.
            </ModalDescription>
          </ModalHeader>

          <div className="px-[24px] py-[24px] space-y-[20px]">
            <div className="space-y-[8px]">
              <Label htmlFor="edit-dashboard-name" className="text-[13px] text-[#08090A]">
                Dashboard Name *
              </Label>
              <Input
                id="edit-dashboard-name"
                size="sm"
                placeholder="e.g., Production GPT-4 Costs"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleEditSave()
                  }
                }}
              />
            </div>
            <div className="space-y-[8px]">
              <Label htmlFor="edit-dashboard-description" className="text-[13px] text-[#08090A]">
                Description
              </Label>
              <Input
                id="edit-dashboard-description"
                size="sm"
                placeholder="Brief description of this dashboard"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setIsEditModalOpen(false)}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
